import React, { useEffect, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { downloadOrgOrdersCsv } from '../../utils/exportCsv';

import { getStatusRowClass } from '../../utils/statusColors';

import { AppSidebar } from '../../components/AppSidebar/AppSidebar';

import { Table } from '../../components/Table/Table';

import { ItemNotesDialog } from '../../components/ItemNotesDialog/ItemNotesDialog';

import { SendBOLDialog, reportBOLSendResult } from '../../components/OrgOrderRow/OrgOrderRow';

import { ManageLoadDialog } from '../../components/OrgOrderRow/ManageLoadDialog';

import { ColumnManager } from '../../components/ColumnManager/ColumnManager';
import { SavedFiltersMenu } from '../../components/SavedFiltersMenu/SavedFiltersMenu';

import { useOrgOrders } from '../../hooks/orders/useOrgOrders';

import { useOrgOrderColumns } from '../../hooks/orders/useOrgOrderColumns';

import { useColumnPreferences } from '../../hooks/table-preferences/useColumnPreferences';

import { useListProducts } from '../../features/products/api';

import { useListLocations } from '../../features/warehouses/api';

import { useSendBOL } from '../../features/carriers/api';

import { toast } from 'react-toastify';

import { AuditLogModal } from '../../components/audit/AuditLogModal';

import { useTableDensity } from '../../hooks/table-preferences/useTableDensity';

import './org-orders.scss';

import {
  useDeleteSavedFilter,
  useListOrgOrderFilters,
  useSavedFilters,
  useSaveFilter,
  exportOrgOrderItems,
  useBulkUpdateOrgItemStatus,
} from '../../features/org-orders/api';

import { OrderBulkActionsBar } from '../../components/OrderBulkActionsBar/OrderBulkActionsBar';

import { PendingOrdersBanner } from '../../components/PendingOrdersBanner/PendingOrdersBanner';

import { useCurrentUser } from '../../features/auth/api';

import { useListOrgMembers } from '../../features/org-members/api';

import { buildSalesRepOptions } from '../../features/org-orders/ordersFilters';

import { statusLabel } from '../../components/OrderBulkActionsBar/bulkStatus';

// Page rules — pure, unit-tested, no React. See features/org-orders/ordersPage.

import {
  ORG_ORDERS_STATUS_OPTIONS,
  PENDING_REVIEW_STATUSES,
  TABLE_ID,
  buildBulkStatusItems,
  buildExportParams,
  findItemIndex,
  flattenExportRows,
  findMatchingSavedFilterId,
  globalPosition,
  hasNextItem as computeHasNextItem,
  hasPreviousItem as computeHasPreviousItem,
  isReviewingPending,
  nextStep,
  previousStep,
  readSavedFilters,
  resolvePendingNavigation,
  summarizeBulkResult,
} from '../../features/org-orders/ordersPage';

import type { PendingPageNavigation } from '../../features/org-orders/ordersPage';

import {
  TableFilterBar,
  TableFilterDefinition,
} from '../../components/TableFilterBar/TableFilterBar';

import { CustomerOrderDrawer } from '../../components/CustomerOrderDrawer/CustomerOrderDrawer';

import { BulkCarrierRatesModal } from '../../components/BulkCarrierRatesModal/BulkCarrierRatesModal';

import SearchInput from '../../components/SearchInput/SearchInput';

import { useFuelSurcharge } from '../../features/fuel-surcharge/api';

export default function OrgOrdersPage() {
  const {
    rows,
    qboInvoice,
    setQboInvoice,

    total,

    totalPages,

    isLoading,

    isFetching,

    status,

    search,

    productId,

    locationId,

    page,

    pageSize,

    setStatus,

    setSearch,

    setProductId,

    setLocationId,

    setPage,

    setPageSize,

    manageModal,

    notesModal,

    openManage,

    closeManage,

    openNotes,

    closeNotes,

    saveItem,

    rejectWithReason,

    approveItem,

    rejectItem,

    updateItem,

    sortBy,

    sortDirection,

    handleSort,

    salesRep,

    setSalesRep,

    deliveryState,

    setDeliveryState,

    pickupState,

    setPickupState,

    pickupCity,

    setPickupCity,

    truckNumber,

    setTruckNumber,

    driverName,

    setDriverName,

    carrierId,

    setCarrierId,

    orderDateFrom,

    setOrderDateFrom,

    orderDateTo,

    setOrderDateTo,

    pickupDateFrom,

    setPickupDateFrom,

    pickupDateTo,

    setPickupDateTo,

    deliveryDateFrom,

    setDeliveryDateFrom,

    deliveryDateTo,

    setDeliveryDateTo,

    loadOption,

    setLoadOption,

    selection,

    tag,

    setTag,
  } = useOrgOrders();

  const { density, toggleDensity } = useTableDensity();

  const { data: fuelSurchargeWeeks = [] } = useFuelSurcharge();

  const [isExporting, setIsExporting] = useState(false);

  const [bulkCarrierRatesOpen, setBulkCarrierRatesOpen] = useState(false);

  const bulkUpdateStatus = useBulkUpdateOrgItemStatus();

  const hasSelection = selection.count > 0;

  // Same count the sidebar badge shows, from the already-cached user query.

  const { data: me } = useCurrentUser();

  const pendingOrderItemCount = (me as any)?.pendingOrderItemCount ?? 0;

  // The banner steps aside once the table is already showing those loads.

  const reviewingPending = isReviewingPending(status);

  // BOL modal state

  const [bolModal, setBolModal] = useState<{ isOpen: boolean; item: any | null }>({
    isOpen: false,

    item: null,
  });

  const sendBOL = useSendBOL();

  const handleSendBOL = (item: any) => {
    setBolModal({ isOpen: true, item });
  };

  const handleSendBOLConfirm = async (emails: string[], note?: string, contactName?: string) => {
    if (!bolModal.item) return;

    try {
      const result = await sendBOL.mutateAsync({
        itemId: bolModal.item.id,

        emails,

        note,

        contactName: contactName || bolModal.item.driverName || undefined,
      });

      reportBOLSendResult(result);

      setBolModal({ isOpen: false, item: null });
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to send BOL');
    }
  };

  const { columns: baseColumns } = useOrgOrderColumns({
    onManage: openManage,

    onViewNotes: openNotes,

    onSendBOL: handleSendBOL,
  });

  // Apply column preferences

  const {
    visibleColumns,

    orderedColumns,

    columnVisibility,

    toggleColumn,

    showAllColumns,

    hideAllColumns,

    reorderColumns,

    isUpdating,
  } = useColumnPreferences({
    tableId: TABLE_ID,

    columns: baseColumns,
  });

  // Load products and locations for filters

  const { data: productsData } = useListProducts({ page: 1, pageSize: 1000 });

  const { data: locationsData } = useListLocations();

  const { data: orderFilters } = useListOrgOrderFilters();

  const deliveryStates = orderFilters?.deliveryStates ?? [];

  const pickupStates = orderFilters?.pickupStates ?? [];

  const pickupCities = orderFilters?.pickupCities ?? [];

  const truckNumbers = orderFilters?.truckNumbers ?? [];

  const driverNames = orderFilters?.driverNames ?? [];

  const carriers = orderFilters?.carriers ?? [];

  const loadOptions = orderFilters?.loadOptions ?? [];

  const tags = orderFilters?.tags ?? [];

  const products = productsData?.products ?? [];

  const locations = locationsData ?? [];

  const [auditOpen, setAuditOpen] = useState(false);

  const savedFiltersQuery = useSavedFilters(TABLE_ID);

  const saveFilter = useSaveFilter(TABLE_ID);

  const deleteSavedFilterMutation = useDeleteSavedFilter(TABLE_ID);

  const savedFilters = savedFiltersQuery.data ?? [];

  const [orderDrawerOpen, setOrderDrawerOpen] = useState(false);

  const currentFilters = {
    qboInvoice,
    salesRep,

    search,

    status,

    productId,

    locationId,

    deliveryState,

    pickupState,

    pickupCity,

    truckNumber,

    driverName,

    carrierId,

    orderDateFrom,

    orderDateTo,

    pickupDateFrom,

    pickupDateTo,

    deliveryDateFrom,

    deliveryDateTo,

    pageSize,

    tag,
  };

  // The saved filter matching the current filters, or '' — derived so it clears
  // itself whenever a filter changes or resets (e.g. navigating back to Orders).
  // BMP_QBO_INVOICE_FILTER_V1: compare existing filter fields using the
  // existing matcher; match the QBO state separately so an old preset (missing
  // this field) is interpreted as All and new presets match correctly.
  const matchingFilters = { ...currentFilters };
  delete (matchingFilters as any).qboInvoice;
  const activeFilterId = findMatchingSavedFilterId(
    matchingFilters,
    savedFilters
      .filter((filter: any) => (filter.filters?.qboInvoice ?? '') === qboInvoice)
      .map((filter: any) => {
        const filters = { ...(filter.filters ?? {}) };
        delete filters.qboInvoice;
        return { ...filter, filters };
      })
  );

  const saveCurrentFilters = async (name: string) => {
    const trimmed = name.trim();

    if (!trimmed) {
      toast.warn('Enter a filter name.');

      return;
    }

    try {
      await saveFilter.mutateAsync({
        name: trimmed,

        filters: currentFilters,
      });

      toast.success('Filter saved.');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to save filter.');
    }
  };

  const loadSavedFilter = (id: string) => {
    try {
      const saved = savedFilters.find((f: any) => f.id === id);

      if (!saved) {
        toast.error('Saved filter not found.');

        return;
      }

      const f = readSavedFilters(saved);

      setSalesRep(f.salesRep);

      setSearch(f.search);

      setStatus(f.status);

      setProductId(f.productId);

      setLocationId(f.locationId);

      setDeliveryState(f.deliveryState);

      setPickupState(f.pickupState);

      setPickupCity(f.pickupCity);

      setTruckNumber(f.truckNumber);

      setDriverName(f.driverName);

      setCarrierId(f.carrierId);

      setOrderDateFrom(f.orderDateFrom);

      setOrderDateTo(f.orderDateTo);

      setPickupDateFrom(f.pickupDateFrom);

      setPickupDateTo(f.pickupDateTo);

      setDeliveryDateFrom(f.deliveryDateFrom);

      setDeliveryDateTo(f.deliveryDateTo);
      const savedQboInvoice = (saved as any)?.filters?.qboInvoice;
      setQboInvoice(
        savedQboInvoice === 'invoiced' || savedQboInvoice === 'not_invoiced' ? savedQboInvoice : ''
      );
      setTag(f.tag);

      setPageSize(f.pageSize);

      setPage(1);

      toast.success(`Loaded "${saved.name}".`);
    } catch {
      toast.error('Failed to load saved filter.');
    }
  };

  const deleteSavedFilter = async (id: string) => {
    if (!id) return;

    try {
      await deleteSavedFilterMutation.mutateAsync(id);

      toast.success('Saved filter deleted.');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to delete saved filter.');
    }
  };

  /** Exports the current selection straight from the rows already in hand. */

  const handleDownloadSelectedCSV = () => {
    setIsExporting(true);

    try {
      downloadOrgOrdersCsv(selection.selectedRows, orderedColumns, fuelSurchargeWeeks);

      toast.success(
        `Exported ${selection.count} selected load${selection.count === 1 ? '' : 's'}.`
      );
    } catch {
      toast.error('Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const handleApplyBulkStatus = async (status: string, rows: any[]) => {
    if (rows.length === 0) return;

    try {
      const { succeeded, failed } = await bulkUpdateStatus.mutateAsync({
        status,

        items: buildBulkStatusItems(rows, status),
      });

      const summary = summarizeBulkResult(succeeded, failed, rows.length, statusLabel(status));

      if (summary.tone === 'success') {
        toast.success(summary.message);
      } else {
        // Keep the selection so the admin can retry the ones that didn't take.

        toast.warn(summary.message);
      }

      if (summary.clearSelection) {
        selection.clear();
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Bulk status update failed');
    }
  };

  const handleDownloadCSV = async () => {
    setIsExporting(true);

    try {
      const result = await exportOrgOrderItems({
        ...buildExportParams({
          status,
          search,
          productId,
          locationId,

          deliveryState,
          pickupState,
          pickupCity,

          truckNumber,
          driverName,

          carrierId,
          loadOption,

          orderDateFrom,
          orderDateTo,

          pickupDateFrom,
          pickupDateTo,

          deliveryDateFrom,
          deliveryDateTo,

          sortBy,
          sortDirection,

          salesRep,
          tag,
        }),
        ...(qboInvoice ? { qboInvoice } : {}),
      });

      if (result.truncated) {
        toast.warn(
          `Export limited to 5,000 rows. ${result.total.toLocaleString()} total matched. Refine your filters to export all.`
        );
      }

      downloadOrgOrdersCsv(flattenExportRows(result.items), orderedColumns, fuelSurchargeWeeks);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const updateLoadedFilter = async (id: string) => {
    const target = savedFilters.find((f: any) => f.id === id);
    if (!target) {
      toast.warn('Load a saved filter first.');

      return;
    }

    try {
      await saveFilter.mutateAsync({
        name: target.name,

        filters: currentFilters,
      });

      toast.success(`Updated "${target.name}".`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to update saved filter.');
    }
  };

  const { data: orgMembers } = useListOrgMembers();

  const salesRepOptions = useMemo(() => buildSalesRepOptions(orgMembers), [orgMembers]);

  const statusOptions = ORG_ORDERS_STATUS_OPTIONS;

  const tableFilters: TableFilterDefinition[] = [
    {
      id: 'status',

      label: 'Status',

      type: 'multiSelect',

      value: status,

      onChange: (value) => {
        setStatus(value);

        setPage(1);
      },

      options: statusOptions,
    },

    {
      id: 'sales-rep',

      label: 'Sales Reps',

      type: 'multiSelect',

      value: salesRep,

      onChange: (value) => {
        setSalesRep(value);

        setPage(1);
      },

      options: salesRepOptions,
    },

    {
      id: 'product',

      label: 'Product',

      type: 'multiSelect',

      value: productId,

      onChange: (value) => {
        setProductId(value);

        setPage(1);
      },

      options: products.map((p: any) => ({
        value: p.id,

        label: p.name,
      })),
    },

    {
      id: 'pickup-location',

      label: 'Warehouse',

      type: 'multiSelect',

      value: locationId,

      onChange: (value) => {
        setLocationId(value);

        setPage(1);
      },

      options: locations.map((l: any) => ({
        value: l.id,

        label: l.name || `${l.line1}, ${l.city}, ${l.state}`,
      })),
    },

    {
      id: 'delivery-state',

      label: 'Delivery State',

      type: 'multiSelect',

      value: deliveryState,

      onChange: (value) => {
        setDeliveryState(value);

        setPage(1);
      },

      options: deliveryStates.map((s: string) => ({
        value: s,

        label: s,
      })),
    },

    {
      id: 'pickup-state',

      label: 'Pickup State',

      type: 'multiSelect',

      value: pickupState,

      onChange: (value) => {
        setPickupState(value);

        setPage(1);
      },

      options: pickupStates.map((s: string) => ({
        value: s,

        label: s,
      })),
    },

    {
      id: 'pickup-city',

      label: 'Pickup City',

      type: 'multiSelect',

      value: pickupCity,

      onChange: (value) => {
        setPickupCity(value);

        setPage(1);
      },

      options: pickupCities.map((c: string) => ({
        value: c,

        label: c,
      })),
    },

    {
      id: 'truck-number',

      label: 'Truck #',

      type: 'multiSelect',

      value: truckNumber,

      onChange: (value) => {
        setTruckNumber(value);

        setPage(1);
      },

      options: truckNumbers.map((t: string) => ({
        value: t,

        label: t,
      })),
    },

    {
      id: 'driver-name',

      label: 'Driver',

      type: 'multiSelect',

      value: driverName,

      onChange: (value) => {
        setDriverName(value);

        setPage(1);
      },

      options: driverNames.map((d: string) => ({
        value: d,

        label: d,
      })),
    },

    {
      id: 'carrier',

      label: 'Carrier',

      type: 'multiSelect',

      value: carrierId,

      onChange: (value) => {
        setCarrierId(value);

        setPage(1);
      },

      options: carriers.map((c: any) => ({
        value: c.id,

        label: c.companyName,
      })),
    },

    {
      id: 'date',

      label: 'Order Date',

      type: 'dateRange',

      value: { from: orderDateFrom, to: orderDateTo },

      onChange: ({ from, to }) => {
        setOrderDateFrom(from);

        setOrderDateTo(to);

        setPage(1);
      },
    },

    {
      id: 'pickup-date',

      label: 'Pickup Date',

      type: 'dateRange',

      value: { from: pickupDateFrom, to: pickupDateTo },

      onChange: ({ from, to }) => {
        setPickupDateFrom(from);

        setPickupDateTo(to);

        setPage(1);
      },
    },

    {
      id: 'delivery-date',

      label: 'Delivery Date',

      type: 'dateRange',

      value: { from: deliveryDateFrom, to: deliveryDateTo },

      onChange: ({ from, to }) => {
        setDeliveryDateFrom(from);

        setDeliveryDateTo(to);

        setPage(1);
      },
    },

    {
      id: 'load-option',

      label: 'Fulfillment method',

      type: 'multiSelect',

      value: loadOption,

      onChange: (value) => {
        setLoadOption(value);

        setPage(1);
      },

      options: loadOptions.map((lo: any) => ({
        value: lo.id,

        label: lo.capacityLabel ? `${lo.name} (${lo.capacityLabel}t)` : lo.name,
      })),
    },

    {
      id: 'tags',

      label: 'Tags',

      type: 'multiSelect',

      value: tag,

      onChange: (value) => {
        setTag(value);

        setPage(1);
      },

      options: tags.map((tag: string) => ({
        value: tag,

        label: tag,
      })),
    },
    {
      id: 'qbo-invoice',
      label: 'QuickBooks Invoice',
      type: 'singleSelect',
      value: qboInvoice,
      defaultValue: '',
      options: [
        { value: '', label: 'All' },
        { value: 'invoiced', label: 'Invoiced in QBO' },
        { value: 'not_invoiced', label: 'Not invoiced in QBO' },
      ],
      onChange: (value) => {
        setQboInvoice(value === 'invoiced' || value === 'not_invoiced' ? value : '');
        setPage(1);
      },
    },
  ];

  const handleOpenReplicatedItem = (newItem: any) => {
    closeManage();

    openManage(newItem);
  };

  /**

   * Open a load picked from the audit modal's "Recent loads" tab. It may not be

   * on the current page or even in the current filter, so only the id is passed

   * — the dialog fetches the load itself.

   */

  const handleOpenLoadFromAudit = (load: { itemId: string }) => {
    setAuditOpen(false);

    openManage({ id: load.itemId });
  };

  /**

   * Deep link from the Orders sidebar flyout: `/orders?load=<itemId>` opens that

   * load's dialog, then the param is dropped so a refresh or Back doesn't reopen

   * it. Other params (filters) are preserved.

   */

  const [pageSearchParams, setPageSearchParams] = useSearchParams();

  useEffect(() => {
    const loadId = pageSearchParams.get('load');

    if (!loadId) return;

    openManage({ id: loadId });

    const next = new URLSearchParams(pageSearchParams);

    next.delete('load');

    setPageSearchParams(next, { replace: true });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSearchParams]);

  const selectedItemIndex = useMemo(
    () => findItemIndex(rows, manageModal.data),

    [rows, manageModal.data]
  );

  const [pendingPageNavigation, setPendingPageNavigation] = useState<PendingPageNavigation>(null);

  /** Applies a navigation step: open a row here, or turn the page and remember why. */

  const applyStep = (step: ReturnType<typeof previousStep>) => {
    if (step.kind === 'open') {
      openManage(rows[step.index]);

      return;
    }

    if (step.kind === 'changePage') {
      setPendingPageNavigation(step.then);

      setPage(step.page);
    }
  };

  const handlePreviousItem = () => applyStep(previousStep(selectedItemIndex, page));

  const handleNextItem = () =>
    applyStep(nextStep(selectedItemIndex, rows.length, page, totalPages));

  const hasPreviousItem = computeHasPreviousItem(selectedItemIndex, page);

  const hasNextItem = computeHasNextItem(selectedItemIndex, rows.length, page, totalPages);

  const currentGlobalPosition = globalPosition(selectedItemIndex, page, pageSize);

  useEffect(() => {
    const resolved = resolvePendingNavigation(pendingPageNavigation, rows, isFetching || isLoading);

    if (resolved.action === 'wait') {
      return;
    }

    if (resolved.action === 'open') {
      openManage(resolved.row);
    }

    setPendingPageNavigation(null);
  }, [pendingPageNavigation, rows, isFetching, isLoading, openManage]);

  return (
    <div className="layout">
      <AppSidebar />

      <div className="card">
        {/* Header */}

        <div className="flex-between" style={{ marginBottom: 12 }}>
          <div className="organization_orders_title">
            <h2 className="card__title">Organization Orders</h2>

            <p className="card__subtitle">
              Showing {rows.length} of {total} total
            </p>
          </div>

          {/* Column Manager + Density toggle */}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={toggleDensity}
              title={density === 'compact' ? 'Switch to normal rows' : 'Switch to compact rows'}
              style={{ fontSize: 13 }}
            >
              {density === 'compact' ? '⊟ Normal' : '⊞ Compact'}
            </button>

            <SavedFiltersMenu
              savedFilters={savedFilters}
              activeFilterId={activeFilterId}
              onApply={loadSavedFilter}
              onDelete={deleteSavedFilter}
              onSaveNew={saveCurrentFilters}
              onUpdate={updateLoadedFilter}
              isSaving={saveFilter.isPending}
              isDeleting={deleteSavedFilterMutation.isPending}
            />

            <ColumnManager
              columns={orderedColumns}
              columnVisibility={columnVisibility}
              onToggleColumn={toggleColumn}
              onShowAll={showAllColumns}
              onHideAll={hideAllColumns}
              onReorderColumn={reorderColumns}
              isUpdating={isUpdating}
            />
          </div>
        </div>

        {/* Filters */}

        <div className="card card--bleed-top" style={{ marginBottom: 16 }}>
          <div
            className="grid"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}
          >
            <div className="form-control">
              <label>Search</label>

              <SearchInput
                placeholder="Product, customer, city, state, carrier, load #, warehouse, fulfillment method, date…"
                value={search}
                onValueChange={(value) => setSearch(value)}
              />
            </div>

            <div className="form-control">
              <label>Page size</label>

              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                {[100, 200, 300].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <TableFilterBar filters={tableFilters} />

        {/* Legend/Key */}

        <div className="flex-between">
          <div className="orders-legend">
            <span className="orders-legend__label">Key:</span>

            <div className="orders-legend__item">
              <span className="orders-legend__icon">$</span>

              <span className="orders-legend__text">= Requires prepayment</span>
            </div>
          </div>

          <div className="page-actions">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => setOrderDrawerOpen(true)}
            >
              + Add Order
            </button>

            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setBulkCarrierRatesOpen(true)}
              title="Download or upload carrier rates in Excel"
            >
              ⇅ Bulk Carrier Rates
            </button>

            {/*

              Hidden while rows are selected: the bulk bar owns the export then,

              so there is only ever one CSV button — and one export scope — on

              screen at a time.

            */}

            {!hasSelection && (
              <button
                type="button"
                className="btn btn--ghost"
                onClick={handleDownloadCSV}
                disabled={rows.length === 0 || isExporting}
                title="Export every row matching your current filters, using your visible columns"
              >
                {isExporting ? 'Exporting…' : `↓ Export All (${total.toLocaleString()})`}
              </button>
            )}

            <button className="btn btn--primary" onClick={() => setAuditOpen(true)}>
              Audit log
            </button>
          </div>
        </div>

        <PendingOrdersBanner
          count={pendingOrderItemCount}
          isReviewing={reviewingPending}
          onReview={() => setStatus(PENDING_REVIEW_STATUSES)}
        />

        {/* Bulk actions — only while rows are selected */}

        {hasSelection && (
          <OrderBulkActionsBar
            selectedRows={selection.selectedRows}
            selectedOffPage={selection.selectedOffPage}
            onClear={selection.clear}
            onExport={handleDownloadSelectedCSV}
            isExporting={isExporting}
            onApplyStatus={handleApplyBulkStatus}
            isApplyingStatus={bulkUpdateStatus.isPending}
          />
        )}

        {/* Table */}

        <Table
          columns={visibleColumns}
          data={rows}
          isLoading={isLoading}
          emptyMessage="No orders found."
          className={`org-orders-table${density === 'compact' ? ' org-orders-table--compact' : ''}`}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={handleSort}
          getRowClassName={(row: any) => getStatusRowClass(row.status)}
          tableId={TABLE_ID}
          selection={{
            isSelected: selection.isSelected,

            onToggleRow: selection.toggleRow,

            onTogglePage: selection.togglePage,

            allPageSelected: selection.allPageSelected,

            somePageSelected: selection.somePageSelected,

            rowLabel: (row: any) => `load ${row.loadNumber ?? row.id}`,
          }}
          mobileCard={{
            titleColumnId: 'load-number',

            trailingColumnId: 'status',

            primaryColumnIds: ['customer', 'product', 'date', 'carrier'],
          }}
        />

        {/* Pagination */}

        {rows.length > 0 && (
          <div className="pager-row">
            <div />

            <div className="pager">
              <button
                className="btn btn--ghost"
                disabled={page <= 1}
                onClick={() => setPage((p: number) => Math.max(1, p - 1))}
              >
                Prev
              </button>

              <span>
                Page {page} / {totalPages}
              </span>

              <button
                className="btn btn--ghost"
                disabled={page >= totalPages}
                onClick={() => setPage((p: number) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {isFetching && (
          <div className="muted" style={{ marginTop: 8 }}>
            Refreshing…
          </div>
        )}

        {/* Manage dialog */}

        {manageModal.data && (
          <ManageLoadDialog
            itemId={manageModal.data.id}
            placeholderItem={manageModal.data}
            onClose={closeManage}
            onSave={saveItem}
            onReject={rejectWithReason}
            isSaving={approveItem.isPending || updateItem.isPending}
            isRejecting={rejectItem.isPending}
            onReplicatedItem={handleOpenReplicatedItem}
            onPrevious={handlePreviousItem}
            onNext={handleNextItem}
            hasPrevious={hasPreviousItem}
            hasNext={hasNextItem}
            currentPosition={currentGlobalPosition}
            totalItems={total}
            isNavigating={pendingPageNavigation !== null || isFetching}
          />
        )}

        {/* Notes dialog */}

        <ItemNotesDialog open={notesModal.isOpen} onClose={closeNotes} item={notesModal.data} />

        {/* BOL dialog */}

        {bolModal.isOpen && bolModal.item && (
          <SendBOLDialog
            item={bolModal.item}
            carrier={bolModal.item.carrier}
            contacts={bolModal.item.carrier?.contacts ?? []}
            assignedContactId={bolModal.item.driverContactId ?? null}
            onCancel={() => setBolModal({ isOpen: false, item: null })}
            onConfirm={handleSendBOLConfirm}
            isSubmitting={sendBOL.isPending}
          />
        )}

        <CustomerOrderDrawer open={orderDrawerOpen} onClose={() => setOrderDrawerOpen(false)} />

        <AuditLogModal
          open={auditOpen}
          onClose={() => setAuditOpen(false)}
          entityType="ORDER"
          onOpenLoad={handleOpenLoadFromAudit}
        />

        <BulkCarrierRatesModal
          open={bulkCarrierRatesOpen}
          onClose={() => setBulkCarrierRatesOpen(false)}
          onImported={() => {}}
        />
      </div>
    </div>
  );
}
